
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
    branch_id: string;
    status: string;
    category: string;
    brand: string;
    model: string;
    manufacture_year: number;
    color: string;
    registration_number: string;
    registration_expiry_date: string;
    vin_chassis_number: string;
    assigned_driver_id: string;
    purchase_date: string;
    purchase_cost: number;
    next_service_due: string;
    insurance_provider: string;
    insurance_policy_number: string;
    insurance_expiry_date: string;

    owner_name: string;
    owner_mobile: string;
    owner_email: string;
    vehicle_image: FileList;
    rc_document: FileList;
    fuel_type: string;
    odometer_reading: number;
    driver_assigned_at: string;
}

interface Option {
    id: number;
    name?: string;
    branch_name?: string;
    assigned_branch_id?: string | number;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditVehicle() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [branches, setBranches] = useState<Option[]>([]);
    const [drivers, setDrivers] = useState<Option[]>([]);
    const [previews, setPreviews] = useState({ vehicle: null as string | null, rc: null as string | null });
    const { hasPermission } = useAuth();
    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors, touchedFields }, reset
    } = useForm<FormValues>();

    const selectedBranchId = watch("branch_id");

    const fetchDriversForBranch = async (branchId: string) => {
        try {
            const response: any = await apiPost("drivers/list", { branch_id: branchId });
            if (Array.isArray(response)) {
                setDrivers(response);
            } else if (response.data) {
                setDrivers(response.data);
            } else {
                setDrivers([]);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
            setDrivers([]);
        }
    };

    useEffect(() => {
        if (selectedBranchId) {
            fetchDriversForBranch(selectedBranchId);
        } else {
            setDrivers([]);
        }

        if (touchedFields.branch_id) {
            setValue("assigned_driver_id", "");
        }
    }, [selectedBranchId, touchedFields.branch_id, setValue]);

    useEffect(() => {
        const fetchData = async () => {
            if (!location.state || !location.state.id) {
                toast.error("No Vehicle ID found");
                navigate(`${process.env.PUBLIC_URL}/vehicle`);
                return;
            }

            try {
                // Fetch Options
                // Fetch Options
                const branchRes = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);

                // Fetch Vehicle Data
                const result = await apiPost("vehicles/viewbyid", { id: location.state.id });

                // Set Values
                setValue("branch_id", result.branch_id);
                setValue("status", result.status);
                setValue("category", result.category);
                setValue("brand", result.brand);
                setValue("model", result.model);
                setValue("manufacture_year", result.manufacture_year);
                setValue("color", result.color);
                setValue("fuel_type", result.fuel_type);
                setValue("registration_number", result.registration_number);
                setValue("registration_expiry_date", result.registration_expiry_date);
                setValue("vin_chassis_number", result.vin_chassis_number);
                setValue("assigned_driver_id", result.assigned_driver_id || "");
                setValue("driver_assigned_at", result.driver_assigned_at);
                setValue("odometer_reading", result.odometer_reading);
                setValue("purchase_date", result.purchase_date);
                setValue("purchase_cost", result.purchase_cost);
                setValue("purchase_cost", result.purchase_cost);
                setValue("next_service_due", result.next_service_due);

                setValue("insurance_provider", result.insurance_provider);
                setValue("insurance_policy_number", result.insurance_policy_number);
                setValue("insurance_expiry_date", result.insurance_expiry_date);

                setValue("owner_name", result.owner_name);
                setValue("owner_mobile", result.owner_mobile);
                setValue("owner_email", result.owner_email);



                // Set Previews
                setPreviews({
                    vehicle: result.vehicle_image ? `${mediaUrl}${result.vehicle_image}` : null,
                    rc: result.rc_document ? `${mediaUrl}${result.rc_document}` : null
                });

                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch data", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [location.state, navigate, setValue]);

    const onSubmit = async (data: FormValues) => {
        setSubmitLoading(true);
        try {
            let vehicleImageName = "";
            let rcDocName = "";

            if (data.vehicle_image && data.vehicle_image[0] instanceof File) {
                vehicleImageName = await uploadFile(data.vehicle_image[0]);
            }
            if (data.rc_document && data.rc_document[0] instanceof File) {
                rcDocName = await uploadFile(data.rc_document[0]);
            }

            const payload: any = {
                ...data,
                id: location.state.id,
            };

            if (vehicleImageName) payload.vehicle_image = vehicleImageName;
            if (rcDocName) payload.rc_document = rcDocName;

            await apiPost("vehicles/update", payload);
            toast.success("Vehicle updated successfully", { autoClose: 1000 });

            setTimeout(() => {
                reset();
                navigate(`${process.env.PUBLIC_URL}/vehicle`);
            }, 1000);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || "An unexpected error occurred";
            toast.error(errorMessage, { autoClose: 1000 });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Edit Vehicle" parent="General" title="Edit Vehicle" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody className="custom-input form-validation">
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Vehicle Information</H5>
                                <hr />
                                <Row className="g-3 mb-4">

                                    <Col md={3}>
                                        <Label>Brand <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="brand"
                                            control={control}
                                            rules={{ required: "Brand is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Toyota" invalid={!!errors.brand} style={{ borderColor: errors.brand ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.brand?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Model <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="model"
                                            control={control}
                                            rules={{ required: "Model is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Camry" invalid={!!errors.model} style={{ borderColor: errors.model ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.model?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Manufacture Year <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="manufacture_year"
                                            control={control}
                                            rules={{
                                                required: "Manufacture Year is required",
                                                min: { value: 1900, message: "Year must be 1900 or later" },
                                                max: { value: new Date().getFullYear(), message: `Year must be ${new Date().getFullYear()} or earlier` }
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder="2023" invalid={!!errors.manufacture_year} style={{ borderColor: errors.manufacture_year ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.manufacture_year?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Color <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="color"
                                            control={control}
                                            rules={{ required: "Color is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="White" invalid={!!errors.color} style={{ borderColor: errors.color ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.color?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Fuel Type <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="fuel_type"
                                            control={control}
                                            rules={{ required: "Fuel Type is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.fuel_type} style={{ borderColor: errors.fuel_type ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Fuel Type</option>
                                                        <option value="Petrol">Petrol</option>
                                                        <option value="Diesel">Diesel</option>
                                                        <option value="Electric">Electric</option>
                                                        <option value="Hybrid">Hybrid</option>
                                                        <option value="CNG">CNG</option>
                                                    </Input>
                                                    <FormFeedback>{errors.fuel_type?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id} style={{ borderColor: errors.branch_id ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Status <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="status"
                                            control={control}
                                            rules={{ required: "Status is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.status} style={{ borderColor: errors.status ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Status</option>
                                                        <option value="Active">Active</option>
                                                        <option value="Inactive">Inactive</option>
                                                        <option value="Maintenance">Maintenance</option>
                                                        <option value="Destroyed">Destroyed</option>
                                                        <option value="Sold">Sold</option>
                                                    </Input>
                                                    <FormFeedback>{errors.status?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Category <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="category"
                                            control={control}
                                            rules={{ required: "Category is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.category} style={{ borderColor: errors.category ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Category</option>
                                                        <option value="Heavy Vehicle">Heavy Vehicle</option>
                                                        <option value="Light Vehicle">Light Vehicle</option>
                                                        <option value="Two Wheeler">Two Wheeler</option>
                                                    </Input>
                                                    <FormFeedback>{errors.category?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={3}>
                                        <Label>Registration Number <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="registration_number"
                                            control={control}
                                            rules={{ required: "Reg No is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="ABC-1234" invalid={!!errors.registration_number} style={{ borderColor: errors.registration_number ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.registration_number?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Registration Expiry <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="registration_expiry_date"
                                            control={control}
                                            rules={{ required: "Expiry Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.registration_expiry_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                        placeholderText="YYYY-MM-DD"
                                                        dateFormat="yyyy-MM-dd"
                                                        // minDate={new Date()}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback className={errors.registration_expiry_date ? "d-block" : ""}>{errors.registration_expiry_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>VIN / Chassis <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vin_chassis_number"
                                            control={control}
                                            rules={{ required: "VIN is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="VIN123..." invalid={!!errors.vin_chassis_number} style={{ borderColor: errors.vin_chassis_number ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.vin_chassis_number?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Assigned Driver <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="assigned_driver_id"
                                            control={control}
                                            rules={{ required: false }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.assigned_driver_id} style={{ borderColor: errors.assigned_driver_id ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Driver</option>
                                                        {drivers.map((d: any) => (
                                                            <option key={d.id} value={d.id}>{d.name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.assigned_driver_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    {watch("assigned_driver_id") && (
                                        <>
                                            <Col md={6}>
                                                <Label>Assign Time <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="driver_assigned_at"
                                                    control={control}
                                                    rules={{ required: "Assign Time is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <DatePicker
                                                                className={`form-control ${errors.driver_assigned_at ? "is-invalid" : ""}`}
                                                                selected={field.value ? new Date(field.value) : null}
                                                                onChange={(date) => field.onChange(date ? date.toISOString() : "")}
                                                                placeholderText="Select Date & Time"
                                                                showTimeSelect
                                                                dateFormat="yyyy-MM-dd HH:mm"
                                                                autoComplete="off"
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                dropdownMode="select"
                                                            />
                                                            <FormFeedback className={errors.driver_assigned_at ? "d-block" : ""}>{errors.driver_assigned_at?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label>Odometer Reading <span className="text-danger">*</span></Label>
                                                <Controller
                                                    name="odometer_reading"
                                                    control={control}
                                                    rules={{ required: "Odometer Reading is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <Input type="number" {...field} placeholder="e.g. 15000" invalid={!!errors.odometer_reading} style={{ borderColor: errors.odometer_reading ? "red" : "" }} autoComplete="off" />
                                                            <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                        </>
                                    )}
                                </Row>

                                <Col xs={12}>
                                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                        <CardHeader tag="h5" style={{
                                            backgroundColor: "#f8f9fa",
                                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                        }}>
                                            Purchase & Service
                                        </CardHeader>
                                        <CardBody>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <Label>Purchase Date <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="purchase_date"
                                                        control={control}
                                                        rules={{ required: "Purchase Date is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <DatePicker
                                                                    className={`form-control ${errors.purchase_date ? "is-invalid" : ""}`}
                                                                    selected={field.value ? new Date(field.value) : null}
                                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                    placeholderText="YYYY-MM-DD"
                                                                    dateFormat="yyyy-MM-dd"
                                                                    peekNextMonth
                                                                    showMonthDropdown
                                                                    showYearDropdown
                                                                    dropdownMode="select"
                                                                    autoComplete="off"
                                                                />
                                                                <FormFeedback className={errors.purchase_date ? "d-block" : ""}>{errors.purchase_date?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Label>Purchase Cost <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="purchase_cost"
                                                        control={control}
                                                        rules={{ required: "Purchase Cost is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <Input type="number" {...field} placeholder="0.00" invalid={!!errors.purchase_cost} style={{ borderColor: errors.purchase_cost ? "red" : "" }} autoComplete="off" />
                                                                <FormFeedback>{errors.purchase_cost?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>

                                                <Col md={6}>
                                                    <Label>Next Service Due <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="next_service_due"
                                                        control={control}
                                                        rules={{ required: "Next Service Date is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <DatePicker
                                                                    className={`form-control ${errors.next_service_due ? "is-invalid" : ""}`}
                                                                    selected={field.value ? new Date(field.value) : null}
                                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                    placeholderText="YYYY-MM-DD"
                                                                    dateFormat="yyyy-MM-dd"
                                                                    // minDate={new Date()}
                                                                    peekNextMonth
                                                                    showMonthDropdown
                                                                    showYearDropdown
                                                                    dropdownMode="select"
                                                                    autoComplete="off"
                                                                />
                                                                <FormFeedback className={errors.next_service_due ? "d-block" : ""}>{errors.next_service_due?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>



                                <Col xs={12}>
                                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                        <CardHeader tag="h5" style={{
                                            backgroundColor: "#f8f9fa",
                                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                        }}>
                                            Insurance Details
                                        </CardHeader>
                                        <CardBody>
                                            <Row className="g-3">
                                                <Col md={4}>
                                                    <Label>Insurance Provider <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="insurance_provider"
                                                        control={control}
                                                        rules={{ required: "Provider is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <Input {...field} placeholder="Provider Name" invalid={!!errors.insurance_provider} style={{ borderColor: errors.insurance_provider ? "red" : "" }} autoComplete="off" />
                                                                <FormFeedback>{errors.insurance_provider?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Policy Number <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="insurance_policy_number"
                                                        control={control}
                                                        rules={{ required: "Policy Number is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <Input {...field} placeholder="Policy Number" invalid={!!errors.insurance_policy_number} style={{ borderColor: errors.insurance_policy_number ? "red" : "" }} autoComplete="off" />
                                                                <FormFeedback>{errors.insurance_policy_number?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Insurance Expiry <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="insurance_expiry_date"
                                                        control={control}
                                                        rules={{ required: "Expiry Date is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <DatePicker
                                                                    className={`form-control ${errors.insurance_expiry_date ? "is-invalid" : ""}`}
                                                                    selected={field.value ? new Date(field.value) : null}
                                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                                    placeholderText="YYYY-MM-DD"
                                                                    dateFormat="yyyy-MM-dd"
                                                                    // minDate={new Date()}
                                                                    peekNextMonth
                                                                    showMonthDropdown
                                                                    showYearDropdown
                                                                    dropdownMode="select"
                                                                    autoComplete="off"
                                                                />
                                                                <FormFeedback className={errors.insurance_expiry_date ? "d-block" : ""}>{errors.insurance_expiry_date?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>

                                <Col xs={12}>
                                    <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                        <CardHeader tag="h5" style={{
                                            backgroundColor: "#f8f9fa",
                                            borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                        }}>
                                            Owner Details
                                        </CardHeader>
                                        <CardBody>
                                            <Row className="g-3">
                                                <Col md={4}>
                                                    <Label>Owner Name <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="owner_name"
                                                        control={control}
                                                        rules={{ required: "Owner Name is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <Input {...field} placeholder="Owner Name" invalid={!!errors.owner_name} style={{ borderColor: errors.owner_name ? "red" : "" }} autoComplete="off" />
                                                                <FormFeedback>{errors.owner_name?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Owner Contact <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="owner_mobile"
                                                        control={control}
                                                        rules={{ required: "Owner Contact is required" }}
                                                        render={({ field }) => (
                                                            <>
                                                                <PhoneInput
                                                                    country={"in"}
                                                                    onlyCountries={["in", "sa", "ae"]}
                                                                    disableDropdown={false}
                                                                    countryCodeEditable={false}
                                                                    value={field.value}
                                                                    onChange={(value) => field.onChange(value)}
                                                                    inputProps={{
                                                                        name: "owner_mobile",
                                                                        placeholder: "Owner Contact",
                                                                        autoComplete: "none",
                                                                    }}
                                                                    inputStyle={{
                                                                        backgroundColor: "var(--input-background-color)",
                                                                        color: "var(--input-text-color)",
                                                                        width: "100%",
                                                                        borderColor: errors.owner_mobile ? "red" : "var(--input-border-color, #e0e0e0)",
                                                                    }}
                                                                    dropdownStyle={{
                                                                        backgroundColor: "var(--input-background-color)",
                                                                        backdropFilter: "blur(1000px)",
                                                                    }}
                                                                    buttonStyle={{
                                                                        backgroundColor: "var(--input-background-color)",
                                                                    }}
                                                                    specialLabel=""
                                                                />
                                                                {errors.owner_mobile && <small className="text-danger">{errors.owner_mobile.message}</small>}
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Owner Email <span className="text-danger">*</span></Label>
                                                    <Controller
                                                        name="owner_email"
                                                        control={control}
                                                        rules={{ required: "Owner Email is required", pattern: { value: /^\S+@\S+$/i, message: "Invalid email format" } }}
                                                        render={({ field }) => (
                                                            <>
                                                                <Input type="email" {...field} placeholder="Owner Email" invalid={!!errors.owner_email} style={{ borderColor: errors.owner_email ? "red" : "" }} autoComplete="off" />
                                                                <FormFeedback>{errors.owner_email?.message}</FormFeedback>
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>

                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <Label>Vehicle Image <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vehicle_image"
                                            control={control}
                                            rules={{
                                                required: !previews.vehicle && "Vehicle Image is required",
                                                validate: (files: any) => {
                                                    if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                        return "File size must be less than 2MB";
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                onChange(e.target.files);
                                                                setPreviews(prev => ({ ...prev, vehicle: URL.createObjectURL(e.target.files![0]) }));
                                                            }
                                                        }}
                                                        invalid={!!errors.vehicle_image}
                                                        style={{ borderColor: errors.vehicle_image ? "red" : "" }}
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback>{errors.vehicle_image?.message}</FormFeedback>
                                                    {previews.vehicle && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={previews.vehicle} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>RC Document <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="rc_document"
                                            control={control}
                                            rules={{
                                                required: !previews.rc && "RC Document is required",
                                                validate: (files: any) => {
                                                    if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                        return "File size must be less than 2MB";
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                onChange(e.target.files);
                                                                setPreviews(prev => ({ ...prev, rc: URL.createObjectURL(e.target.files![0]) }));
                                                            }
                                                        }}
                                                        invalid={!!errors.rc_document}
                                                        style={{ borderColor: errors.rc_document ? "red" : "" }}
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback>{errors.rc_document?.message}</FormFeedback>
                                                    {previews.rc && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={previews.rc} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                {hasPermission('vehicle.edit') && (
                                    <Btn color="primary" disabled={submitLoading} type="submit">
                                        {submitLoading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                    </Btn>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div >
    );
}
